import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption, TSelectValue } from '@private/components';
import { NOTIF_TYPES } from '@private/notifications';

import Button from 'components/Button';
import { NAVIGATION_ACTION } from 'components/Grids/DataGrid/Filters/utils/types';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useBreakpoint } from 'hooks/adaptive/useBreakpoint';
import { getPresets, IGetPresetsResult } from 'hooks/usePageNavigation/helpers';
import { IUrlFilterSettings } from 'hooks/usePageNavigation/types';
import { useUrlSearchQuery } from 'hooks/usePageNavigation/useUrlSearchQuery';
import { useSaveInURL } from 'hooks/useSaveInURL';
import {
  useActivateFiltersPreset,
  useGetPortalUserConfig,
  useSaveFiltersPreset,
} from 'queries/portalUser';
import { notificationService } from 'utils/notificationService';

import { useFilterContext } from '../FilterContext';
import { MAX_PRESETS_LIMIT } from '../utils/constants';

import { getFiltersToSave, REDIRECTED_PRESET } from './helpers';
import PresetOption from './PresetOption';
import { PresetsSelect } from './styled';
import { useSavePresetModal } from './useSavePresetModal';

const Presets = () => {
  const { t } = useTranslation();

  const checkWindowWidth = useBreakpoint();
  const urlSearchQuery = useUrlSearchQuery();
  const { savedData: urlFilters } = useSaveInURL<IUrlFilterSettings>('pageFilters');

  const { data: portalUser } = useGetPortalUserConfig();
  const { mutate: activatePreset } = useActivateFiltersPreset();
  const { mutate: savePreset } = useSaveFiltersPreset();

  const {
    config: { failureCount, ignoreList },
    filters: pageFiltersSearch,
    gridColumnsSettings,
    isRedirectedPreset,
    lastNavigationAction,
    pageKey,
    rollBackBrokenFilters,
    setIsRedirectedPreset,
  } = useFilterContext();

  const [showPresetModal] = useSavePresetModal();

  const { activeFilterPresetId: activeSavedPresetId, filterPresets } =
    useMemo<IGetPresetsResult>(() => {
      return getPresets({ pageKey, portalUser });
    }, [pageKey, portalUser]);

  const [activePresetId, setActivePresetId] = useState(
    isRedirectedPreset ? REDIRECTED_PRESET : activeSavedPresetId,
  );

  useEffect(() => {
    if (failureCount && failureCount > 0 && pageKey && activeSavedPresetId !== null) {
      rollBackBrokenFilters?.();
      if (
        lastNavigationAction &&
        [
          NAVIGATION_ACTION.ALL,
          NAVIGATION_ACTION.FILTER,
          NAVIGATION_ACTION.PRESET,
        ].includes(lastNavigationAction)
      ) {
        activatePreset(
          { pageKey, presetId: null },
          {
            onSuccess: () => {
              notificationService.show({
                dismissIn: 10_000,
                message: t('portalUser.filtersPresets.apply.warning'),
                title: t('notifications.warningTitle'),
                type: NOTIF_TYPES.WARNING,
              });
            },
          },
        );
      }
    }
  }, [failureCount]);

  useEffect(() => {
    if (isRedirectedPreset) {
      setActivePresetId(REDIRECTED_PRESET);
    } else {
      setActivePresetId(activeSavedPresetId);
    }
  }, [isRedirectedPreset, activeSavedPresetId]);

  const presetOptions = useMemo<ISelectOption[]>(() => {
    const defaultOption = {
      label: t('common.filters.preset.base'),
      value: null,
    };

    const urlPreset = {
      label: t('common.filters.preset.redirectedPreset'),
      value: REDIRECTED_PRESET,
    };

    const presets = filterPresets.map(({ id, isSystem, name }) => ({
      customProps: {
        isSystem,
      },
      label: t(name as never),
      value: id,
    }));

    return urlFilters?.search || urlSearchQuery
      ? [urlPreset, defaultOption, ...presets]
      : [defaultOption, ...presets];
  }, [t, filterPresets, urlFilters?.search, urlSearchQuery]);

  const onChangePreset = useCallback(
    (id: TSelectValue) => {
      const newId = (id as string) || null;

      const isRedirectedPreset = id === REDIRECTED_PRESET;

      setIsRedirectedPreset?.(isRedirectedPreset);

      const currentFilter = isRedirectedPreset
        ? urlFilters?.search
        : filterPresets.find((preset) => preset.id === newId)?.filters || {};

      if (newId === activePresetId || !currentFilter) {
        return;
      }

      if (pageKey && filterPresets && !isRedirectedPreset) {
        activatePreset({ pageKey, presetId: newId });
      }

      setActivePresetId(newId);
    },
    [
      setIsRedirectedPreset,
      urlFilters?.search,
      filterPresets,
      activePresetId,
      pageKey,
      activatePreset,
    ],
  );

  const onSavePresets = useCallback(() => {
    const filters = getFiltersToSave({ ignoreList, search: pageFiltersSearch });
    const filtersCount = Object.keys(filters).length || 0;

    if (pageKey) {
      showPresetModal({
        filtersCount,
        onConfirm: ({ name }, helpers, closeModal) => {
          savePreset(
            { columns: gridColumnsSettings, filters, name, pageKey },
            {
              onSettled: () => helpers.setSubmitting(false),
              onSuccess: (resp) => {
                setActivePresetId(resp.id);
                closeModal();
              },
            },
          );
        },
      });
    }
  }, [
    ignoreList,
    pageFiltersSearch,
    pageKey,
    showPresetModal,
    savePreset,
    gridColumnsSettings,
  ]);

  return (
    <>
      <PresetsSelect
        onChange={onChangePreset}
        options={presetOptions || []}
        renderOption={(props) => <PresetOption {...props} pageKey={pageKey} />}
        size={checkWindowWidth(1000) ? 'md' : 'lg'}
        testIds={{
          arrowWrapper: 'presetSelectArrowWrapper',
          input: 'presetSelectInput',
          removeAllButton: 'presetSelectRemoveButton',
        }}
        value={activePresetId}
      />
      <Button
        closeTooltipIn={0}
        iconSize={20}
        onClick={onSavePresets}
        showTooltipOnClick={presetOptions?.length !== MAX_PRESETS_LIMIT}
        size={checkWindowWidth(1000) ? 'md' : 'lg'}
        startIcon={<ICONS_MAP.SaveFilter />}
        testIds={{ container: 'saveFiltersButton' }}
        tooltipText={
          presetOptions?.length === MAX_PRESETS_LIMIT
            ? t('common.filters.limit')
            : undefined
        }
        type='button'
        variant='outlined'
      />
    </>
  );
};

export default appReactMemo(Presets);
